// @flow
import PubSub from 'pubsub-js'
import type { Effect, Event } from 'effector'
import { forward } from 'effector'
import type { Fail } from 'core/types'
import { decamelize } from 'utils/camelize'
import { USER_FETCHED } from 'core/constants/effector'
import { notifications } from 'utils/notifications'
import type {
  AllUserFields,
  ApiUser,
  ApiUserMe,
  ApiUserProfile,
  UserType,
  UserGetParam,
  PossibleAccountFieldsCamel,
} from '../types'
import * as api from '../api'
import { USER_GET_PARAMS } from '../constants'
import { domain } from './domain'

export const initialize: Event<void> = domain.createEvent('inintialize user store')

export const updateUser: Event<$Shape<AllUserFields>> = domain.createEvent('update user from redux')

export const changeUser: Event<$Shape<AllUserFields>> = domain.createEvent('change user data at the store')

export const reset: Event<void> = domain.createEvent('reset user data')

export const getUserFx: Effect<UserGetParam, ApiUser, Fail> = domain.createEffect('get user', {
  async handler(param) {
    const user = api.get(param)

    return user
  },
})

export const getUserMeFx: Effect<void, ApiUserMe, Fail> = domain.createEffect('get user', {
  async handler() {
    const user = api.getUserMe()

    return user
  },
})

export const getUserProfileFx: Effect<void, ApiUserProfile, Fail> = domain.createEffect('get user', {
  async handler() {
    const user = api.getUserProfile()

    return user
  },
})

export const updateProfileFx: Effect<$Shape<PossibleAccountFieldsCamel>, ApiUser, Fail> = domain.createEffect(
  'update user profile',
  {
    async handler(values) {
      await api.updateUserProfile(values)

      const user = await getUserProfileFx()

      return user
    },
  }
)

export const convertUserTypeFx: Effect<UserType, ApiUserProfile, Fail> = domain.createEffect('convert user type', {
  async handler(value) {
    await api.convertUserType(value)

    const user = await getUserProfileFx()

    return user
  },
})

export const changeUserGradeFx: Effect<number, void, Fail> = domain.createEffect("change user's grade", {
  async handler(gradeId) {
    await api.changeUserGrade(gradeId)
    await getUserProfileFx()
  },
})

export const uploadAvatarFx: Effect<FormData, void, Fail | { avatar: string }> = domain.createEffect(
  "Upload user's avatar effect",
  {
    async handler(data) {
      await api.uploadAvatar(data)
    },
  }
)

export const confirmEmailFx: Effect<void, void> = domain.createEffect('confirm user email', {
  async handler() {
    return api.confirmEmail()
  },
})

export const changeUserSchoolEntryYearFx: Effect<number, void, Fail> = domain.createEffect(
  "change user's school entry year",
  {
    async handler(year) {
      await api.changeUserSchoolEntryYear(year)
      await getUserProfileFx()
    },
  }
)

getUserFx.done.watch(({ params, result }) => {
  // публикуем в редакс что юзер загружен
  const user = decamelize(result)

  PubSub.publish(USER_FETCHED, {
    param: params,
    user,
  })
})

getUserMeFx.doneData.watch((result) => {
  // публикуем в редакс что юзер загружен
  const user = decamelize(result)

  PubSub.publish(USER_FETCHED, {
    param: USER_GET_PARAMS[0],
    user,
  })
})

getUserProfileFx.doneData.watch((result) => {
  // публикуем в редакс что юзер загружен
  const user = decamelize(result)

  PubSub.publish(USER_FETCHED, {
    param: USER_GET_PARAMS[1],
    user,
  })
})

// Update profile after successful uploading
forward({
  from: uploadAvatarFx.done,
  to: getUserProfileFx.prepend(() => undefined),
})

updateProfileFx.fail.watch(() => {
  notifications.error('Произошла ошибка при обновлении данных пользователя')
})

uploadAvatarFx.fail.watch(({ error }) => {
  if (error) {
    if (error.avatar !== undefined) {
      notifications.error(error.avatar)
    } else if (error.error !== undefined) {
      notifications.error(error.error)
      // $FlowFixMe[prop-missing]
    } else if (error.code === 'ECONNABORTED') {
      notifications.error('Не удалось загрузить аватар. Попробуйте обновить страницу')
    }
  }
})
confirmEmailFx.done.watch(() => notifications.success('На ваш email высланы инструкции для подтверждения регистрации'))
confirmEmailFx.fail.watch((error) => notifications.error(error))

getUserFx.fail.watch(() => {
  notifications.error('Произошла ошибка при получении данных пользователя')
})
